import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Calendar as CalendarIcon, ListChecks, Pill, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function RemindersPanel({ chestId }) {
  const [rem, setRem] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/hope-chests/${chestId}/reminders`).then((r) => setRem(r.data)).catch(() => {});
  }, [chestId]);

  const sendDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hope-chests/${chestId}/digest/send`);
      if (data.sent) toast.success("Today's digest sent to your inbox");
      else toast.message("Digest prepared (email service not yet confirmed — check Settings)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  if (!rem) return null;
  const total =
    (rem.upcoming_appointments?.length || 0) +
    (rem.due_tasks?.length || 0) +
    (rem.refills_soon?.length || 0) +
    (rem.today_events?.length || 0);

  return (
    <div className="hc-card p-6" data-testid="reminders-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#E8DCC8] flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#3A5A4A]" />
          </div>
          <div>
            <h3 className="font-display text-lg">Today's reminders</h3>
            <p className="text-xs text-[#7A736E]">{total === 0 ? "All quiet — enjoy the calm." : `${total} thing${total > 1 ? 's' : ''} that need your attention`}</p>
          </div>
        </div>
        <Button onClick={sendDigest} disabled={busy} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="send-digest-button">
          <Mail className="w-4 h-4 mr-2" /> Email me today's digest
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={CalendarIcon} title="Upcoming appointments" empty="None in the next 48 hours.">
          {rem.upcoming_appointments?.map((a, i) => (
            <li key={i} className="flex justify-between gap-2 py-1.5 text-sm border-b border-[#EBE5DF] last:border-0">
              <span><strong>{a.doctor}</strong>{a.specialty ? ` • ${a.specialty}` : ""}</span>
              <span className="text-xs text-[#7A736E]">{a._when?.slice(0, 16).replace("T", " at ")}</span>
            </li>
          ))}
        </Section>
        <Section icon={ListChecks} title="Tasks due" empty="Nothing overdue.">
          {rem.due_tasks?.map((t, i) => (
            <li key={i} className="py-1.5 text-sm border-b border-[#EBE5DF] last:border-0">
              <strong>{t.title}</strong>{t.due_date && <span className="text-xs text-[#7A736E]"> • due {t.due_date}</span>}
            </li>
          ))}
        </Section>
        <Section icon={Pill} title="Refills coming up" empty="Pharmacy's not calling.">
          {rem.refills_soon?.map((m, i) => (
            <li key={i} className="py-1.5 text-sm border-b border-[#EBE5DF] last:border-0">
              <strong>{m.name}</strong>{m.refill_date && <span className="text-xs text-[#C87B67]"> • refill by {m.refill_date}</span>}
            </li>
          ))}
        </Section>
        <Section icon={CalendarIcon} title="Today's planner" empty="Nothing scheduled.">
          {rem.today_events?.map((e, i) => (
            <li key={i} className="py-1.5 text-sm border-b border-[#EBE5DF] last:border-0">
              <strong>{e.title}</strong>{e.time && <span className="text-xs text-[#7A736E]"> • {e.time}</span>}
            </li>
          ))}
        </Section>
      </div>
      <div className="mt-4 text-right">
        <Link to={`/planner/${chestId}`} className="text-xs text-[#3A5A4A] hover:underline">Open Hope Planner →</Link>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, empty }) {
  const items = Array.isArray(children) ? children : [children];
  const has = items.filter(Boolean).length > 0;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#3A5A4A]" />
        <p className="text-sm font-medium">{title}</p>
      </div>
      {has ? <ul>{children}</ul> : <p className="text-xs text-[#7A736E]">{empty}</p>}
    </div>
  );
}
